import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { Textarea } from 'primeng/textarea';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DateService } from 'src/app/services/date.service';
import { MessageService } from 'src/app/services/message.service';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { KeepaGraphComponent } from '../parts/keepa-graph/keepa-graph.component';
import {
  RemoveUserAmazonTrackingGQL,
  UpsertUserAmazonTrackingGQL,
  UserAmazonTrackingsGQL,
  UserAmazonTrackingsQuery,
} from 'src/gql/generated';
import { gqlErrors } from 'src/app/utils/gql-errors';

type Tracking = UserAmazonTrackingsQuery['userAmazonTrackings'][number];

@Component({
  selector: 'app-amazon-tracking',
  templateUrl: './amazon-tracking.component.html',
  styleUrls: ['./amazon-tracking.component.sass'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PanelModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    DialogModule,
    Textarea,
    KeepaGraphComponent,
  ],
})
export class AmazonTrackingComponent implements OnInit {
  trackings: Tracking[] = [];
  selectedTrackings: Tracking[] = [];
  loading = false;
  saving = false;

  // 編集ダイアログ (Unit 6)
  showEditDialog = false;
  currentTracking: Tracking | null = null;
  memoText = '';
  probabilityInput: number | null = null;

  constructor(
    private router: Router,
    private routingService: RoutingService,
    private subtitleService: SubtitleService,
    private userService: UserService,
    private dateService: DateService,
    private messageService: MessageService,
    private userAmazonTrackingsGQL: UserAmazonTrackingsGQL,
    private upsertUserAmazonTrackingGQL: UpsertUserAmazonTrackingGQL,
    private removeUserAmazonTrackingGQL: RemoveUserAmazonTrackingGQL,
  ) {
    this.routingService.loginRequired();
    this.userService.amazonPriceTrackingRequired();
    this.subtitleService.setSubTitle('Amazon追跡一覧');
  }

  async ngOnInit(): Promise<void> {
    await this.loadTrackings();
  }

  async loadTrackings(): Promise<void> {
    this.loading = true;
    try {
      const result = await firstValueFrom(this.userAmazonTrackingsGQL.fetch({ fetchPolicy: 'network-only' }));
      if (gqlErrors(result)?.length > 0) {
        this.messageService.setFlashMessageWithClear(
          'error',
          'エラー',
          gqlErrors(result)[0].message || 'データの取得に失敗しました',
        );
        this.trackings = [];
        return;
      }
      this.trackings = (result.data?.userAmazonTrackings ?? []) as Tracking[];
    } catch (error) {
      this.messageService.setFlashMessageWithClear('error', 'エラー', 'データの取得に失敗しました');
      console.error('Error loading trackings:', error);
      this.trackings = [];
    } finally {
      this.loading = false;
    }
  }

  navigateToRegister(): void {
    this.router.navigate(['/amazon-tracking/register']);
  }

  formatDate(dateString: string): string {
    return this.dateService.formatDate(new Date(dateString), 'YYYY/MM/DD HH:mm');
  }

  probabilityColorClass(p: number | null | undefined): string {
    if (p == null) return '';
    if (p >= 80) return 'text-red-500 font-bold';
    if (p >= 50) return 'text-orange-500';
    return 'text-color-secondary';
  }

  // --- 編集 (Unit 6) ---

  openEditDialog(tracking: Tracking): void {
    this.currentTracking = tracking;
    this.memoText = tracking.memo ?? '';
    this.probabilityInput = tracking.probability ?? null;
    this.showEditDialog = true;
  }

  closeEditDialog(): void {
    this.showEditDialog = false;
    this.currentTracking = null;
    this.memoText = '';
    this.probabilityInput = null;
  }

  async saveEdit(): Promise<void> {
    if (!this.currentTracking) return;
    if (this.saving) return;

    this.saving = true;
    try {
      const result = await firstValueFrom(
        this.upsertUserAmazonTrackingGQL.mutate({
          variables: {
            asin: this.currentTracking.amazonProduct.asin,
            memo: this.memoText ? this.memoText : null,
            probability: this.probabilityInput,
          },
        }),
      );

      if (gqlErrors(result)?.length > 0) {
        this.messageService.setFlashMessageWithClear(
          'error',
          'エラー',
          gqlErrors(result)[0].message || '保存に失敗しました',
        );
        return;
      }

      if (result.data?.upsertUserAmazonTracking === true) {
        const targetAsin = this.currentTracking.amazonProduct.asin;
        const newMemo = this.memoText ? this.memoText : null;
        const newProb = this.probabilityInput;
        this.trackings = this.trackings.map((t) =>
          t.amazonProduct.asin === targetAsin ? { ...t, memo: newMemo, probability: newProb } : t,
        );
        this.messageService.setFlashMessageWithClear('success', '成功', '保存しました');
        this.closeEditDialog();
      } else {
        this.messageService.setFlashMessageWithClear('error', 'エラー', '保存に失敗しました');
      }
    } catch (error) {
      this.messageService.setFlashMessageWithClear('error', 'エラー', '保存に失敗しました');
      console.error('Error saving tracking:', error);
    } finally {
      this.saving = false;
    }
  }

  // --- 削除 ---

  async deleteTracking(tracking: Tracking): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.removeUserAmazonTrackingGQL.mutate({
          variables: {
            asin: tracking.amazonProduct.asin,
          },
        }),
      );

      if (gqlErrors(result)?.length > 0) {
        this.messageService.setFlashMessageWithClear(
          'error',
          'エラー',
          gqlErrors(result)[0].message || '削除に失敗しました',
        );
        return;
      }

      if (result.data?.removeUserAmazonTracking === true) {
        this.trackings = this.trackings.filter((t) => t.amazonProduct.asin !== tracking.amazonProduct.asin);
        this.selectedTrackings = this.selectedTrackings.filter(
          (t) => t.amazonProduct.asin !== tracking.amazonProduct.asin,
        );
        this.messageService.setFlashMessageWithClear('success', '成功', '削除しました');
      } else {
        this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
      }
    } catch (error) {
      this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
      console.error('Error deleting tracking:', error);
    }
  }

  async deleteSelected(): Promise<void> {
    if (this.selectedTrackings.length === 0) return;

    this.loading = true;
    let successCount = 0;
    let failCount = 0;
    const toDelete = [...this.selectedTrackings];
    const deletedAsins = new Set<string>();

    for (const tracking of toDelete) {
      try {
        const result = await firstValueFrom(
          this.removeUserAmazonTrackingGQL.mutate({
            variables: {
              asin: tracking.amazonProduct.asin,
            },
          }),
        );
        if (result.data?.removeUserAmazonTracking === true) {
          successCount++;
          deletedAsins.add(tracking.amazonProduct.asin);
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
        console.error('Error deleting tracking:', error);
      }
    }

    if (deletedAsins.size > 0) {
      this.trackings = this.trackings.filter((t) => !deletedAsins.has(t.amazonProduct.asin));
    }
    this.selectedTrackings = [];
    this.loading = false;

    if (failCount === 0) {
      this.messageService.setFlashMessageWithClear('success', '成功', `${successCount}件削除しました`);
    } else if (successCount > 0) {
      this.messageService.setFlashMessageWithClear('warn', '警告', `${successCount}件削除、${failCount}件失敗`);
    } else {
      this.messageService.setFlashMessageWithClear('error', 'エラー', '削除に失敗しました');
    }
  }
}
